/**
 * Global Cache Initializer
 * 
 * This module handles the initialization of browser storage caches
 * at the application level. It ensures that localStorage is available
 * and ready to use before any component attempts to access it.
 */

// Export these constants for direct usage in cache clearing methods
export const CACHE_PREFIX = 'moc_assistant';
export const CACHE_INITIALIZED_KEY = `${CACHE_PREFIX}_cache_initialized`;
export const INIT_TIMESTAMP_KEY = `${CACHE_PREFIX}_init_timestamp`;
const MAX_INIT_RETRIES = 3;
const RETRY_DELAY = 100; // ms

// Global initialization state
let isInitialized = false;

/**
 * Initialize the application cache system
 * This should be called as early as possible in the application lifecycle
 */
export const initializeCache = (): boolean => {
  // If already initialized, return immediately without logging
  if (isInitialized) {
    return true;
  }

  // Only log initial cache setup, not subsequent checks
  try {
    // Check if we're in a browser environment with localStorage
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('[CacheInitializer] localStorage not available');
      return false;
    }
    
    // Perform a test write/read/delete to verify localStorage is working
    try {
      const timestamp = Date.now().toString();
      localStorage.setItem(INIT_TIMESTAMP_KEY, timestamp);
      const testResult = localStorage.getItem(INIT_TIMESTAMP_KEY);
      
      if (testResult === timestamp) {
        // Mark as initialized for future reference
        localStorage.setItem(CACHE_INITIALIZED_KEY, 'true');
        isInitialized = true;
        
        // Ensure all browsers get a clean cache state on page load
        // This forces the browser to acknowledge our storage operations
        // Fix for Safari and some mobile browsers that might defer storage ops
        document.cookie = `${CACHE_PREFIX}_init=${timestamp};path=/;max-age=3600`;
        
        return true;
      } else {
        console.warn('[CacheInitializer] Test write/read failed');
        return false;
      }
    } catch (storageError) {
      console.error('[CacheInitializer] Storage operation failed:', storageError);
      return false;
    }
  } catch (error) {
    console.error('[CacheInitializer] Cache initialization failed:', error);
    return false;
  }
};

/**
 * Perform a cache operation with retry logic
 * @param operation - Function containing the storage operation to perform
 * @returns Promise resolving to a boolean indicating success
 */
export const reliableCacheOperation = async (operation: () => void): Promise<boolean> => {
  // Ensure cache is initialized before proceeding
  if (!initializeCache()) {
    console.warn('[CacheInitializer] Cannot perform operation: cache not initialized');
    return false;
  }
  
  let retries = 0;
  while (retries < MAX_INIT_RETRIES) {
    try {
      // Execute the operation
      operation();
      
      // Perform an extra verification to ensure browser has committed the change
      // This helps with some browsers that might defer writes
      if (typeof document !== 'undefined') {
        // Force a microtask to complete, giving the browser a chance to flush changes
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      
      return true;
    } catch (error) {
      console.warn(`[CacheInitializer] Operation failed (retry ${retries + 1}/${MAX_INIT_RETRIES}):`, error);
      retries++;
      
      if (retries >= MAX_INIT_RETRIES) {
        console.error('[CacheInitializer] Operation failed after maximum retries');
        return false;
      }
      
      // Wait before retrying with increasing backoff
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retries + 1)));
    }
  }
  
  return false;
};

/**
 * Get a cache item with retry logic
 * @param key - Storage key
 * @returns The stored value or null if not found
 */
export const getCacheItem = (key: string): string | null => {
  if (!initializeCache()) {
    return null;
  }
  
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`[CacheInitializer] Failed to get cache item '${key}':`, error);
    return null;
  }
};

/**
 * Set a cache item with retry logic
 * @param key - Storage key
 * @param value - Value to store
 * @returns Promise resolving to a boolean indicating success
 */
export const setCacheItem = async (key: string, value: string): Promise<boolean> => {
  return await reliableCacheOperation(() => {
    localStorage.setItem(key, value);
  });
};

/**
 * Remove a cache item with retry logic
 * @param key - Storage key to remove
 * @returns Promise resolving to a boolean indicating success
 */
export const removeCacheItem = async (key: string): Promise<boolean> => {
  return await reliableCacheOperation(() => {
    localStorage.removeItem(key);
  });
};

/**
 * Clear all cached items matching a specific prefix pattern
 * @param prefixes - Array of prefixes to match for clearing
 * @returns Promise resolving to a boolean indicating success
 */
export const clearCacheWithPrefixes = async (prefixes: string[]): Promise<boolean> => {
  if (!initializeCache()) {
    return false;
  }
  
  try {
    // Collect all keys that match our prefixes
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const shouldRemove = prefixes.some(prefix => key.startsWith(prefix));
        if (shouldRemove) {
          keysToRemove.push(key);
        }
      }
    }
    
    // Remove all matched keys with our reliable cache operation
    let allSucceeded = true;
    
    for (const key of keysToRemove) {
      const success = await reliableCacheOperation(() => {
        localStorage.removeItem(key);
      });
      
      if (!success) {
        allSucceeded = false;
      }
    }
    
    return allSucceeded;
  } catch (error) {
    console.error('[CacheInitializer] Failed to clear cache with prefixes:', error);
    return false;
  }
};

/**
 * Clear all cached items completely
 * @returns Promise resolving to a boolean indicating success
 */
export const clearAllCache = async (): Promise<boolean> => {
  if (!initializeCache()) {
    return false;
  }
  
  try {
    // First try using clear() directly as it's more efficient
    await reliableCacheOperation(() => {
      localStorage.clear();
    });
    
    // Double-check if clear() worked properly by counting remaining items
    const remainingItems = localStorage.length;
    if (remainingItems > 0) {
      // Some items might still be there, try individual removal
      const allKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) allKeys.push(key);
      }
      
      // Remove each item individually
      for (const key of allKeys) {
        await reliableCacheOperation(() => {
          localStorage.removeItem(key);
        });
      }
    }
    
    // Re-initialize the cache system after clearing
    initializeCache();
    
    return localStorage.length === 0 || localStorage.length === 1; // Allow for just the initialization key
  } catch (error) {
    console.error('[CacheInitializer] Failed to clear all cache:', error);
    return false;
  }
};

// Initialize the cache immediately when this module is imported
initializeCache();

export default {
  initializeCache,
  reliableCacheOperation,
  getCacheItem,
  setCacheItem,
  removeCacheItem,
  clearCacheWithPrefixes,
  clearAllCache
};